import dayjs from "dayjs";

import { DeepSkyType, StrapiComponentType, StrapiCollectionType } from "@/src/type";
import { AvailabilityStatusTypes } from "@/src/type/deepsky/asset/PropertyInventory";
import { MoneyHelper, StringHelper } from "@/src/util";

/**
 * format the price
 */
function priceFormatter(currency?: string, price?: number) {
  if (!price || !currency) {
    return "";
  }
  const currencySymbol = MoneyHelper.getCurrencySymbol(currency);
  return `${currencySymbol}${StringHelper.addCommasToNumber(`${price}`)}`;
}

/**
 * get the min price from the unit prices
 */
function getMinPrice(
  unit: DeepSkyType.AssetType.PropertyInventory.Unit,
  minPricePeriod: string,
  propertyUnitDetails: StrapiComponentType.Section.PropertyUnitDetails
) {
  const price = unit?.prices?.filter((price) => price.pricePeriod === minPricePeriod);
  if ((price ?? []).length === 0) {
    return {};
  }

  const minPrice = price.reduce((min, price) => (price.basePrice < min.basePrice ? price : min), price[0]);
  if (!minPrice?.basePrice || minPrice.basePrice === 0) {
    return {};
  }

  const currencySymbol = MoneyHelper.getCurrencySymbol(minPrice.currency);
  const from = propertyUnitDetails?.formTitle ?? "From";
  const priceString = `${currencySymbol}${StringHelper.addCommasToNumber(`${minPrice.basePrice ?? ""}`)}`;
  const years = propertyUnitDetails?.intervalYearsTitleShort;
  const months = propertyUnitDetails?.intervalMonthsTitleShort;
  const interval = propertyUnitDetails?.showIntervalOnPriceCard && minPrice.pricePeriod === "P12M" ? years : months;
  return {
    label: `${from} ${priceString}`,
    labelWithInterval: `${from} ${priceString}${interval ? ` / ${interval}` : ""}`,
    price: priceString,
    term: minPrice.leaseDuration,
  };
}

/**
 * clean the duration string to a number
 */
const cleanDuration = (duration: string) => {
  try {
    return parseInt(duration.replace("P", "").replace("M", "").replace("Y", ""), 10);
  } catch (error) {
    return 0;
  }
};

/**
 * get sorted prices
 */
function getSortedPricesByDuration(unit: DeepSkyType.AssetType.PropertyInventory.Unit, minPricePeriod: string) {
  const prices = unit?.prices?.filter((price) => price.pricePeriod === minPricePeriod);
  return prices.sort((a, b) => cleanDuration(a.leaseDuration) - cleanDuration(b.leaseDuration));
}

/**
 * get the unit amenities from the unit attributes
 */
function getUnitAmenities(unit: DeepSkyType.AssetType.PropertyInventory.Unit) {
  const amenities = unit?.attributes?.find((attribute) => attribute["unit.amenities"]);
  const options = (amenities?.["unit.amenities"] ?? "").split(",").map((amenity) => amenity.trim());
  return options.filter((option) => (option ?? "").length > 0);
}

/**
 * get the available date from the unit attributes
 */
function getAvailableDate(unit: DeepSkyType.AssetType.PropertyInventory.Unit, propertyUnitDetails: StrapiComponentType.Section.PropertyUnitDetails) {
  if (
    !unit?.availabilityStatus?.status ||
    unit.availabilityStatus.status === AvailabilityStatusTypes.AvailableNow ||
    unit.availabilityStatus.status === AvailabilityStatusTypes.AvailablePendingUnitTurnDate
  ) {
    return propertyUnitDetails.availableNowTitle ?? "Available Now";
  }

  if (!unit?.availabilityStatus?.date) {
    return "";
  }

  try {
    const availableDate = dayjs(unit.availabilityStatus.date).tz("Etc/UTC");
    const tomorrow = dayjs().tz("Etc/UTC").add(1, "day");
    if (availableDate.isBefore(tomorrow)) {
      return propertyUnitDetails.availableNowTitle ?? "Available Now";
    }
    return availableDate.format("MMM DD, YYYY");
  } catch (error) {}

  return "";
}

/**
 * format the price interval
 */
function priceIntervalFormatter(propertyUnitDetails?: StrapiComponentType.Section.PropertyUnitDetails, pricePeriod?: string) {
  const years = propertyUnitDetails?.intervalYearsTitle ?? "years";
  const months = propertyUnitDetails?.intervalMonthsTitle ?? "months";
  return (pricePeriod ?? "").replace("P", "").replace("M", ` ${months}`).replace("Y", ` ${years}`);
}

/**
 * get the min price for a bedroom type
 * this is used to flatten data for template expressions
 */
function getMinPriceForBedroomType(floorplans: StrapiCollectionType.PropertyFloorplan[], bedroomCount: number): number | null {
  const matchingFloorplans = floorplans.filter((floorplan) => floorplan.bedrooms === bedroomCount);

  if (matchingFloorplans.length === 0) {
    return null;
  }

  const minPrice = matchingFloorplans.reduce(
    (min, floorplan) => {
      const price = floorplan.startingAtPrice;
      if (!price || price === 0) {
        return min;
      }
      return min === null || price < min ? price : min;
    },
    null as number | null
  );

  return minPrice;
}

/**
 * get the prices map for all bedroom types
 * this is used to flatten data for template expressions
 */
function getPricesMapForAllBedroomTypes(
  floorplans: StrapiCollectionType.PropertyFloorplan[]
): Record<string, { minValue: number | null; currency: string }> {
  const bedroomCounts = Array.from(new Set(floorplans.map((floorplan) => floorplan.bedrooms ?? 0))).sort((a, b) => a - b);

  const defaultCurrency = floorplans.find((fp) => fp.startingAtCurrency)?.startingAtCurrency ?? "USD";

  const minPricesMap: Record<string, { minValue: number | null; currency: string }> = {};

  for (const bedroomCount of bedroomCounts) {
    const minPrice = getMinPriceForBedroomType(floorplans, bedroomCount);

    const bedroomFloorplans = floorplans.filter((fp) => fp.bedrooms === bedroomCount);
    const currency = bedroomFloorplans.find((fp) => fp.startingAtCurrency)?.startingAtCurrency ?? defaultCurrency;

    minPricesMap[`b${bedroomCount}`] = {
      minValue: minPrice,
      currency: currency,
    };
  }

  return minPricesMap;
}

export default {
  priceFormatter,
  getMinPrice,
  getSortedPricesByDuration,
  getUnitAmenities,
  getAvailableDate,
  priceIntervalFormatter,
  getMinPriceForBedroomType,
  getPricesMapForAllBedroomTypes,
};
